// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell, Check, FileCheck2, MessageSquare,
  Building2, Database, KeyRound, AlertTriangle, AlertCircle, Info,
} from 'lucide-react'
import * as api from '../api/client'
import { friendlyError } from '../api/errors'
import { ErrorBanner, LoadingState, EmptyState, formatTimestamp } from '../components/workflow/StatePills'
import { useAuth } from '../auth/AuthContext'
import { usePharmacy } from '../auth/PharmacyContext'
import { usePaged, Pager, DEFAULT_PAGE_SIZE } from '../components/Pager'

const SAAS_LAST_SEEN_KEY = 'owlrx.saasNotifications.lastSeen.v1'

/**
 * WI-896 mode-aware Notifications page.
 *
 * Pharmacy mode: Rx notification feed (PA submissions, comments) — pulled
 * from /api/notifications, scoped to the active pharmacy.
 *
 * SaaS mode: platform alerts (onboarding stuck, suspended, parse errors,
 * api keys expiring, feedback backlog) — pulled from /api/saas/notifications.
 * NO Rx, patient, or pharmacy-scoped data.
 */
export default function NotificationsPage() {
  const { mode } = usePharmacy()
  return mode === 'saas' ? <SaasNotificationsView /> : <PharmacyNotificationsView />
}

// ============================================================
// SaaS-mode view (WI-896)
// ============================================================

function SaasNotificationsView() {
  const navigate = useNavigate()
  const { refresh: refreshMe } = useAuth()
  const [data, setData] = useState<api.SaasNotifications | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [severityFilter, setSeverityFilter] = useState<'all' | 'error' | 'warn' | 'info'>('all')

  // D-SUPPORT-7: the SaaS view showed platform ALERTS only, so a support agent's own
  // notifications — the case activity for customers they cover — had nowhere to appear at all.
  // They are a different feed (dbo.notifications, per account) from platform alerts, and the
  // support tier is always in SaaS mode, so this page is where they belong.
  const [mine, setMine] = useState<api.NotificationItem[]>([])

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listSaasNotifications()
      setData(r)
      // Mark "seen" so the bell badge clears next /auth/me cycle
      localStorage.setItem(SAAS_LAST_SEEN_KEY, new Date().toISOString())
      await refreshMe()
    } catch (e) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }
  // Personal feed loads independently: a failure here must not blank the platform alerts, and
  // vice versa. Coupling them in one try is what turned a single 403 into an empty Platform
  // overview earlier in this workstream.
  async function reloadMine() {
    try { const r = await api.getNotifications(false, 20); setMine(r.items) }
    catch { /* personal feed unavailable; platform alerts still render */ }
  }
  useEffect(() => { reload(); reloadMine() }, [])

  async function openMine(n: api.NotificationItem) {
    if (!n.is_read) { try { await api.markNotificationsRead({ ids: [n.id] }) } catch { /* silent */ } }
    setMine(m => m.map(x => x.id === n.id ? { ...x, is_read: true } : x))
    if (n.link) navigate(n.link)
  }

  const items = (data?.items || []).filter(a =>
    severityFilter === 'all' ? true : a.severity === severityFilter)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(items, DEFAULT_PAGE_SIZE, severityFilter)

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={titleStyle}>
            <Bell size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
            Platform notifications
          </h1>
          <div style={subtitleStyle}>
            SaaS administration view — your own notifications, then platform-wide alerts.
            Pharmacy-scoped Rx notifications are not shown here.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <SeverityChip
            label="all" count={data?.count ?? 0}
            active={severityFilter === 'all'}
            onClick={() => setSeverityFilter('all')}
          />
          <SeverityChip
            label="error" count={data?.counts_by_severity.error ?? 0}
            active={severityFilter === 'error'} sev="error"
            onClick={() => setSeverityFilter('error')}
          />
          <SeverityChip
            label="warn" count={data?.counts_by_severity.warn ?? 0}
            active={severityFilter === 'warn'} sev="warn"
            onClick={() => setSeverityFilter('warn')}
          />
          <SeverityChip
            label="info" count={data?.counts_by_severity.info ?? 0}
            active={severityFilter === 'info'} sev="info"
            onClick={() => setSeverityFilter('info')}
          />
          <button onClick={reload} style={chipBtn}>Refresh</button>
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      {/* Your own notifications — support case activity for the customers you cover. Rendered
          above platform alerts because it is work assigned to YOU, where the alerts below are
          fleet-wide conditions. Hidden entirely when empty so it costs nothing for an admin who
          has none. */}
      {mine.length > 0 && (
        <div style={{ ...cardStyle, marginBottom: 16 }}>
          <div style={{ padding: '10px 14px', borderBottom: '1px solid var(--color-border)', fontSize: 13, fontWeight: 700 }}>
            Your notifications
            <span style={{ fontWeight: 400, color: 'var(--color-text-mute)' }}>
              {' '}— {mine.filter(n => !n.is_read).length} unread
            </span>
          </div>
          {mine.map(n => (
            <button key={n.id} onClick={() => openMine(n)}
              style={{
                display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
                padding: '10px 14px', border: 'none', borderTop: '1px solid var(--color-border)',
                background: n.is_read ? 'transparent' : 'var(--color-bg)', color: 'var(--color-text)',
              }}>
              <div style={{ fontSize: 13, fontWeight: n.is_read ? 500 : 700 }}>{n.title}</div>
              {n.body && <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginTop: 2 }}>{n.body}</div>}
              <div style={{ fontSize: 11, color: 'var(--color-text-mute)', marginTop: 3 }}>
                {new Date(n.created_at).toLocaleString()}
              </div>
            </button>
          ))}
        </div>
      )}

      <div style={cardStyle}>
        {loading ? <LoadingState label="Loading platform alerts…" /> :
         items.length === 0 ? (
           <EmptyState message={
             severityFilter === 'all'
               ? "All quiet. No platform alerts."
               : `No ${severityFilter} alerts.`} />
         ) :
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="alerts" />
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {pageRows.map(a => (
            <li key={a.id} style={{
              padding: '14px 16px', borderBottom: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'flex-start', gap: 12,
              background: severityBg(a.severity),
            }}>
              <div style={{ marginTop: 2 }}>
                <KindIcon kind={a.kind} severity={a.severity} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  alignItems: 'baseline', flexWrap: 'wrap', gap: 8,
                }}>
                  <strong style={{ fontSize: 13, color: severityFg(a.severity) }}>
                    {a.title}
                  </strong>
                  <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>
                    {formatTimestamp(a.created_at)}
                  </span>
                </div>
                <div style={{ fontSize: 13, marginTop: 6, color: 'var(--color-text-soft)' }}>
                  {a.detail}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <button style={openBtn} onClick={() => navigate(a.link)}>
                    {linkLabel(a.kind)} →
                  </button>
                  <code style={kindBadgeStyle}>{a.kind}</code>
                </div>
              </div>
            </li>
          ))}
        </ul>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="alerts" />
        </>}
      </div>

      {data?.generated_at && (
        <div style={{ marginTop: 12, fontSize: 11, color: 'var(--color-text-mute)', textAlign: 'right' }}>
          Generated {formatTimestamp(data.generated_at)}
        </div>
      )}
    </div>
  )
}

function SeverityChip({ label, count, active, sev, onClick }: {
  label: string
  count: number
  active: boolean
  sev?: 'error' | 'warn' | 'info'
  onClick: () => void
}) {
  const fg = sev ? severityFg(sev) : 'var(--color-text)'
  return (
    <button onClick={onClick} style={{
      ...chipBtn,
      borderColor: active ? fg : 'var(--color-border)',
      color: active ? fg : 'var(--color-text-soft)',
      fontWeight: active ? 600 : 500,
    }}>
      {label} <span style={{ opacity: 0.7, marginLeft: 4 }}>{count}</span>
    </button>
  )
}

function KindIcon({ kind, severity }: { kind: string; severity: string }) {
  const color = severityFg(severity)
  if (kind.startsWith('pharmacy.')) return <Building2 size={16} style={{ color }} aria-hidden="true" />
  if (kind.startsWith('pioneerrx.')) return <Database size={16} style={{ color }} aria-hidden="true" />
  if (kind.startsWith('api_key.')) return <KeyRound size={16} style={{ color }} aria-hidden="true" />
  if (kind.startsWith('feedback.')) return <MessageSquare size={16} style={{ color }} aria-hidden="true" />
  if (severity === 'error') return <AlertCircle size={16} style={{ color }} aria-hidden="true" />
  if (severity === 'warn') return <AlertTriangle size={16} style={{ color }} aria-hidden="true" />
  return <Info size={16} style={{ color }} aria-hidden="true" />
}

function linkLabel(kind: string): string {
  if (kind.startsWith('pharmacy.')) return 'Open pharmacy'
  if (kind.startsWith('pioneerrx.')) return 'Open PioneerRx'
  if (kind.startsWith('api_key.')) return 'Open API keys'
  if (kind.startsWith('feedback.')) return 'Open feedback'
  return 'Open'
}

function severityFg(sev: string): string {
  switch (sev) {
    case 'error': return '#991b1b'
    case 'warn':  return '#a16207'
    case 'info':  return '#1e40af'
  }
  return 'var(--color-text)'
}
function severityBg(sev: string): string {
  switch (sev) {
    case 'error': return '#fef2f2'
    case 'warn':  return '#fffbeb'
    case 'info':  return 'transparent'
  }
  return 'transparent'
}

// ============================================================
// Pharmacy-mode view (existing behavior, unchanged)
// ============================================================

function PharmacyNotificationsView() {
  const navigate = useNavigate()
  const { refresh: refreshMe } = useAuth()
  const [items, setItems] = useState<api.RxNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [unreadOnly, setUnreadOnly] = useState(true)
  const { page, setPage, pageSize, setPageSize, pageRows, total, totalPages } = usePaged(items, DEFAULT_PAGE_SIZE, String(unreadOnly))

  async function reload() {
    setLoading(true); setError(null)
    try {
      const r = await api.listNotifications(unreadOnly)
      // Defensive: if backend says we're in saas mode (e.g. role/mode
      // mismatch between client and server), bail to empty rather than
      // try to render Rx-shaped fields off saas alerts.
      if (r.mode === 'saas') {
        setItems([])
      } else {
        setItems(r.items)
      }
    } catch (e: any) { setError(friendlyError(e)) }
    finally { setLoading(false) }
  }

  useEffect(() => { reload() }, [unreadOnly])

  async function ack(id: string) {
    try {
      await api.ackNotification(id)
      await reload()
      await refreshMe()
    } catch (e: any) { setError(friendlyError(e)) }
  }

  async function ackAll() {
    try {
      await Promise.all(items.filter(n => !n.acknowledged_at).map(n => api.ackNotification(n.id)))
      await reload()
      await refreshMe()
    } catch (e: any) { setError(friendlyError(e)) }
  }

  return (
    <div style={pageStyle}>
      <header style={headerStyle}>
        <div>
          <h1 style={titleStyle}><Bell size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} /> Notifications</h1>
          <div style={subtitleStyle}>{items.length} {unreadOnly ? 'unread' : 'total'}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => setUnreadOnly(u => !u)} style={chipBtn}>
            {unreadOnly ? 'Show all' : 'Unread only'}
          </button>
          {unreadOnly && items.length > 0 && (
            <button onClick={ackAll} style={primaryBtn}>
              <Check size={13} aria-hidden="true" /> Mark all as read
            </button>
          )}
        </div>
      </header>

      {error && <ErrorBanner message={error} onClose={() => setError(null)} />}

      <div style={cardStyle}>
        {loading ? <LoadingState label="Loading…" /> :
         items.length === 0 ? <EmptyState message={unreadOnly ? 'No unread notifications.' : 'No notifications.'} /> :
        <>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} onPageSize={setPageSize} label="notifications" />
        <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
          {pageRows.map(n => (
            <li key={n.id} style={{
              padding: '14px 16px', borderBottom: '1px solid var(--color-border)',
              display: 'flex', alignItems: 'flex-start', gap: 12,
              background: n.acknowledged_at ? 'transparent' : '#fffbeb',
            }}>
              <div style={{ marginTop: 2 }}>
                {n.kind === 'pa_submitted'
                  ? <FileCheck2 size={16} style={{ color: '#2563eb' }} aria-hidden="true" />
                  : <MessageSquare size={16} style={{ color: 'var(--color-text-soft)' }} aria-hidden="true" />}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 8 }}>
                  <strong style={{ fontSize: 13 }}>
                    {n.kind === 'pa_submitted' ? 'PA submitted' : 'New comment'}
                    {' · '}
                    {n.author_display_name ?? 'system'}
                    {n.author_role && <span style={{ fontWeight: 400, color: 'var(--color-text-mute)', marginLeft: 4, fontSize: 11 }}>({n.author_role})</span>}
                  </strong>
                  <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>{formatTimestamp(n.created_at)}</span>
                </div>
                <div style={{ fontSize: 13, marginTop: 4, color: 'var(--color-text-soft)' }}>
                  {n.entity_type === 'patient'
                    ? `Patient comment · ${n.patient_name}`
                    : `Rx ${n.rx_number ?? '—'} · ${n.drug_name} ${n.drug_strength} · ${n.patient_name}`}
                </div>
                <div style={{ fontSize: 13, marginTop: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {n.snippet}
                </div>
                <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
                  {n.entity_type === 'patient'
                    ? <button style={openBtn} onClick={() => navigate(`/patients/${n.patient_id}`)}>Open patient →</button>
                    : <button style={openBtn} onClick={() => navigate(`/rx/${n.rx_item_id}`)}>Open Rx →</button>}
                  {!n.acknowledged_at && (
                    <button style={ackBtn} onClick={() => ack(n.id)}>
                      <Check size={12} aria-hidden="true" /> Mark as read
                    </button>
                  )}
                  {n.acknowledged_at && (
                    <span style={{ fontSize: 11, color: 'var(--color-text-mute)' }}>
                      Read by {n.acknowledged_by} · {formatTimestamp(n.acknowledged_at)}
                    </span>
                  )}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <Pager page={page} totalPages={totalPages} total={total} pageSize={pageSize} onPage={setPage} showPageSize={false} label="notifications" />
        </>}
      </div>
    </div>
  )
}

// ============================================================
// Shared styles
// ============================================================

const pageStyle: React.CSSProperties = { maxWidth: 'var(--page-max, 900px)', margin: '0 auto', padding: '24px 24px 48px' }
const headerStyle: React.CSSProperties = {
  marginBottom: 16, display: 'flex',
  alignItems: 'baseline', justifyContent: 'space-between',
  flexWrap: 'wrap', gap: 12,
}
const titleStyle: React.CSSProperties = {
  fontFamily: 'Georgia, "Source Serif Pro", serif',
  fontSize: 26, fontWeight: 700, margin: 0, letterSpacing: -0.5,
}
const subtitleStyle: React.CSSProperties = { marginTop: 4, color: 'var(--color-text-soft)', fontSize: 13 }
const cardStyle: React.CSSProperties = {
  background: 'var(--color-surface, #fff)', border: '1px solid var(--color-border)',
  borderRadius: 8, overflow: 'hidden',
}
const buttonBase: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 4,
  padding: '6px 12px', fontSize: 12, fontWeight: 500,
  borderRadius: 4, cursor: 'pointer', fontFamily: 'inherit',
}
const chipBtn: React.CSSProperties = {
  ...buttonBase, background: '#fff', border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
}
const primaryBtn: React.CSSProperties = {
  ...buttonBase, background: 'var(--color-primary, #0d9488)', color: '#fff', border: 'none',
}
const openBtn: React.CSSProperties = {
  ...buttonBase, background: 'transparent', border: '1px solid var(--color-border)',
  color: 'var(--color-text-soft)',
}
const ackBtn: React.CSSProperties = {
  ...buttonBase, background: '#dcfce7', border: '1px solid #86efac', color: '#166534',
}
const kindBadgeStyle: React.CSSProperties = {
  fontFamily: 'monospace', fontSize: 10.5,
  padding: '1px 6px', borderRadius: 3,
  background: 'rgba(0,0,0,0.04)', color: 'var(--color-text-soft)',
}
